import { useEffect, useState, type CSSProperties } from "react";
import { isAudioEnabled, setAudioEnabled, playSpeakeasyChime } from "../../lib/audio";

export interface IncomingAlert {
  id: string;
  type: "call" | "message" | "urgent";
  title: string;
  subtitle: string;
  phone?: string;
  onAccept?: () => void;
  onDismiss?: () => void;
}

interface Props {
  alert: IncomingAlert | null;
  onDismiss: () => void;
}

export default function IncomingCallBanner({ alert, onDismiss }: Props) {
  const [soundOn, setSoundOn] = useState(isAudioEnabled());

  useEffect(() => {
    if (!alert) return;

    if (alert.type === "call") {
      playSpeakeasyChime("call");
      const interval = setInterval(() => {
        playSpeakeasyChime("call");
      }, 3000);
      return () => clearInterval(interval);
    } else if (alert.type === "urgent") {
      playSpeakeasyChime("urgent");
    } else {
      playSpeakeasyChime("message");
    }
  }, [alert]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setAudioEnabled(next);
  };

  if (!alert) return null;

  return (
    <div style={styles.bannerWrap} role="alert" aria-live="assertive">
      <div style={styles.bannerCard}>
        <div style={styles.iconCircle}>
          {alert.type === "call" ? "📞" : alert.type === "urgent" ? "⚠️" : "💬"}
        </div>
        <div style={styles.content}>
          <div style={styles.titleRow}>
            <span style={styles.typeBadge}>
              {alert.type === "call" ? "INCOMING CALL" : alert.type === "urgent" ? "URGENT ALERT" : "NEW MESSAGE"}
            </span>
            <span style={styles.titleText}>{alert.title}</span>
          </div>
          <p style={styles.subtitleText}>{alert.subtitle}</p>
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.soundToggle}
            onClick={toggleSound}
            title={soundOn ? "Mute audio chimes" : "Unmute audio chimes"}
          >
            {soundOn ? "🔔" : "🔕"}
          </button>

          {alert.onAccept && (
            <button
              type="button"
              style={styles.acceptBtn}
              onClick={() => {
                alert.onAccept?.();
                onDismiss();
              }}
            >
              {alert.type === "call" ? "Answer" : "View"}
            </button>
          )}

          <button
            type="button"
            style={styles.dismissBtn}
            onClick={() => {
              alert.onDismiss?.();
              onDismiss();
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  bannerWrap: {
    position: "fixed",
    top: 20,
    right: 20,
    zIndex: 9999,
    maxWidth: "460px",
    width: "calc(100vw - 40px)",
    animation: "fadeIn 0.2s ease-out",
  },
  bannerCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#181410",
    border: "1px solid var(--gold, #F9AF36)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.7), 0 0 15px rgba(249, 175, 54, 0.2)",
    borderRadius: 12,
    padding: "12px 16px",
    color: "#F7F5F0",
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(249, 175, 54, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  typeBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: "#0D0B08",
    background: "var(--gold, #F9AF36)",
    padding: "2px 6px",
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  titleText: {
    fontSize: 14,
    fontWeight: 700,
    color: "#FFFFFF",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitleText: {
    margin: "3px 0 0 0",
    fontSize: 12,
    color: "#A39D98",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  soundToggle: {
    background: "transparent",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: 6,
    padding: "6px 8px",
    fontSize: 13,
    cursor: "pointer",
    color: "#A39D98",
  },
  acceptBtn: {
    background: "#27ae60",
    border: "none",
    borderRadius: 6,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    color: "#FFFFFF",
    cursor: "pointer",
  },
  dismissBtn: {
    background: "rgba(255, 255, 255, 0.08)",
    border: "none",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 600,
    color: "#A39D98",
    cursor: "pointer",
  },
};
